import { useState } from 'react';

import { Box, Typography } from '@mui/material';

import { useTranslate } from 'src/locales';
import { uploadFileToGCS } from 'src/pages/chat/utils/file-upload-gsc';
import { usePutPresignedMediaUrlMutation } from 'src/pages/chat/slice/chatApi';

import { Iconify } from '../iconify'; // Adjust path

type EditorImageUploadProps = {
  editor: any;
};

export function EditorImageUpload({ editor }: EditorImageUploadProps) {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [putPresignedMediaUrl] = usePutPresignedMediaUrlMutation();
  const { t } = useTranslate('translation');

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ✅ Restrict file size to 1MB (1MB = 1024 * 1024 bytes)
    const MAX_SIZE_MB = 1;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setUploadError(t('FILE_SIZE_LIMIT_MB'));
      e.target.value = '';
      return;
    }

    setUploadError(null);
    setLoading(true);

    try {
      // Step 1: Get presigned URL for upload
      const { url, mimeType, publicUrl } = await putPresignedMediaUrl({
        fileName: file.name,
        fileType: file.type,
        isPublic: true,
      }).unwrap();

      // Step 2: Upload image to GCS
      await uploadFileToGCS(file, url, mimeType);

      // Step 3: Set image in editor
      if (publicUrl) {
        editor?.commands.setImage({ src: publicUrl });
      }
    } catch (error) {
      console.error(error);
      setUploadError(t('UPLOAD_FAILED'));
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const hasImage = editor?.getHTML().includes('<img');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <input
          type="file"
          id="image-upload"
          accept="image/*"
          style={{ display: 'none' }}
          disabled={hasImage || loading}
          onChange={handleImageUpload}
        />

        <label
          htmlFor="image-upload"
          style={{
            cursor: hasImage || loading ? 'not-allowed' : 'pointer',
            marginTop: 6,
            opacity: hasImage || loading ? 0.5 : 1,
            pointerEvents: hasImage || loading ? 'none' : 'auto',
          }}
        >
          <Iconify icon="ic-outline-image" width="24px" height="24px" />
        </label>
      </Box>

      {uploadError && (
        <Typography variant="caption" color="error">
          {uploadError}
        </Typography>
      )}
    </Box>
  );
}
